/**
 * Нормализация строки поиска по справочникам — ОДНА на все каталоги (AK-564).
 *
 * Правило пришло из UAT культур: «Боронование» и «Борoнование», «Лен» и «Лён»
 * — для агронома это одно слово, и поиск обязан их находить. У культур логика
 * была написана инлайном (`.toLowerCase().replace(/ё/g,'е')`), у операций её не
 * было вовсе — и один и тот же поиск в двух справочниках работал по-разному.
 *
 * Регистр приводим через `toLowerCase`, а не `localeCompare`: сравниваем
 * подстроку, а не порядок. Символ «ё» схлопываем в «е» в ОБЕ стороны — и в
 * запросе, и в проверяемом значении, поэтому helper применяется к обоим.
 */
export function normalizeSearch(value: string): string {
  return value.toLowerCase().replace(/ё/g, 'е');
}

/** Нашлась ли `query` внутри `value` по правилам справочника (ё=е, регистр). */
export function matchesSearch(value: string, query: string): boolean {
  const q = normalizeSearch(query.trim());
  if (!q) return true;
  return normalizeSearch(value).includes(q);
}

/**
 * Ранг совпадения: чем меньше число, тем выше строка в выпадашке (AK-657).
 *
 * Одного «нашлось / не нашлось» мало, когда у позиции есть КОРОТКОЕ обозначение
 * и длинная расшифровка. UAT founder 18.08 на единицах измерения: запрос «л»
 * возвращал алфавитный список всего, где встречается буква «л» — «г/л»,
 * «килограмм», «километр», — а собственно «л — литр» тонул в середине. Это не
 * ошибка фильтра (все они честно содержат «л»), это отсутствие ранжирования.
 *
 * Порядок групп — от самого точного к самому слабому:
 *
 * 0. обозначение точно равно запросу («л» → «л»);
 * 1. обозначение начинается с запроса («л» → «л/га»);
 * 2. слово расшифровки начинается с запроса («лит» → «литр»);
 * 3. запрос внутри обозначения («л» → «г/л», «мл»);
 * 4. запрос внутри расшифровки («л» → «килограмм») — хвост списка.
 *
 * Внутри группы порядок задаёт вызывающий (у нас — алфавит), поэтому сортировку
 * тут не делаем. Пустой запрос = все в группе 0, список остаётся алфавитным.
 * Не совпало — :data:`SEARCH_RANK_NONE`, такие строки вызывающий отбрасывает.
 *
 * @param query что ввёл человек
 * @param primary короткое обозначение («л», «кг/га») — то, что он, скорее всего, и набирает
 * @param secondary расшифровка («литр», «килограмм на гектар»)
 */
export const SEARCH_RANK_NONE = Number.POSITIVE_INFINITY;

export function searchRank(query: string, primary: string, secondary = ''): number {
  const q = normalizeSearch(query.trim());
  if (!q) return 0;
  const head = normalizeSearch(primary);
  const tail = normalizeSearch(secondary);

  if (head === q) return 0;
  if (head.startsWith(q)) return 1;
  // Слово расшифровки, а не любая позиция внутри: «л» не должно поднимать
  // «килограмм» до уровня «литра» — ровно с этого началась жалоба.
  if (tail && tail.split(/[^\p{L}\p{N}]+/u).some((word) => word && word.startsWith(q))) return 2;
  if (head.includes(q)) return 3;
  if (tail.includes(q)) return 4;
  return SEARCH_RANK_NONE;
}

/**
 * Единый плейсхолдер поля поиска.
 *
 * Было «Поиск (ё=е, регистр не важен)…»: техжаргон в подписи контрола, который
 * ещё и обрезался многоточием на узких экранах. Нормализация ё=е и регистра —
 * это ПОВЕДЕНИЕ поиска (см. `matchesSearch` выше), а не обещание, которое надо
 * зачитывать пользователю (design review 2026-08-18, зона 2 · А5).
 */
export const SEARCH_PLACEHOLDER = 'Поиск по названию';
